import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import pluralize from 'pluralize'
import { type JSX, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { WorkspaceMutators } from '~/mutators/workspaces'
import { useTRPC } from '~/services/trpc'
import { SettingsActions, SettingsContent, useSettingTitle } from '~/templates/settings'

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormErrorAlert,
  FormFieldInput,
  Icon,
  Search,
  processFormError,
} from '@hap/components'

export const Route = createFileRoute('/_authenticated/$alias/settings/workspaces/')({
  component: RouteComponent,
})

function RouteComponent(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const trpc = useTRPC()
  const [newWorkspaceDialogOpen, setNewWorkspaceDialogOpen] = useState(false)

  const query = ((location.search as Record<string, unknown>).query as string) || ''

  const { data: workspaces, isLoading } = useQuery(trpc.workspaces.getAll.queryOptions())

  const filteredWorkspaces = useMemo(() => {
    if (!workspaces || !query) return workspaces
    const q = query.toLowerCase()
    return workspaces.filter((workspace) => workspace.name.toLowerCase().includes(q))
  }, [workspaces, query])

  useSettingTitle(['Workspaces'])

  return (
    <>
      <SettingsActions>
        <Button onClick={() => setNewWorkspaceDialogOpen(true)}>New Workspace</Button>
      </SettingsActions>
      <SettingsContent spacing="sm" className="w-full overflow-y-auto">
        <header className="flex items-center justify-between gap-4">
          <Search
            placeholder="Search workspaces"
            onSearch={(value) =>
              navigate({
                to: location.pathname,
                search: {
                  ...(location.search as Record<string, unknown>),
                  query: value || undefined,
                  page: 1,
                },
                replace: true,
              })
            }
            defaultValue={query}
          />
        </header>

        <div className="grid gap-3">
          {isLoading && (
            <div className="text-muted-foreground py-12 text-center">
              Loading workspaces...
            </div>
          )}
          {!isLoading && filteredWorkspaces?.length === 0 && (
            <div className="text-muted-foreground py-12 text-center">
              {query ? 'No workspaces match your search.' : 'No workspaces yet.'}
            </div>
          )}
          {filteredWorkspaces?.map((workspace) => {
            const scorecardCount = workspace.scorecards?.length || 0
            const channelCount = workspace.channelIds?.length || 0

            return (
              <button
                onClick={() =>
                  navigate({
                    to: `/$alias/settings/workspaces/${workspace.id}`,
                    search: location.search,
                  })
                }
                key={workspace.id}
                className="hover:bg-accent flex items-center justify-between gap-4 rounded-md border p-4 text-left"
              >
                <div className="shrink grow">
                  <p className="flex items-center gap-2 text-lg font-semibold">
                    {workspace.name}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <Badge variant="outline">
                    {scorecardCount} {pluralize('scorecard', scorecardCount)}
                  </Badge>
                  <Badge variant="outline">
                    {channelCount} {pluralize('channel', channelCount)}
                  </Badge>
                  <Icon name="caret-right" className="text-muted-foreground" />
                </div>
              </button>
            )
          })}
        </div>

        <NewWorkspaceDialog
          open={newWorkspaceDialogOpen}
          onOpenChange={setNewWorkspaceDialogOpen}
          onSuccess={(workspaceId) => {
            navigate({
              to: `/$alias/settings/workspaces/${workspaceId}`,
              search: location.search,
            })
          }}
        />
      </SettingsContent>
    </>
  )
}

const newWorkspaceSchema = z.object({
  name: z.string().min(1, 'Please enter a workspace name'),
})

interface NewWorkspaceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (workspaceId: string) => void
}

function NewWorkspaceDialog({ open, onOpenChange, onSuccess }: NewWorkspaceDialogProps) {
  const { create, isPending } = WorkspaceMutators.useCreate({
    onSuccess: (workspaceId) => {
      form.reset()
      onOpenChange(false)
      onSuccess?.(workspaceId)
    },
  })

  const form = useForm({
    defaultValues: { name: '' },
    resolver: zodResolver(newWorkspaceSchema),
  })

  const handleSubmit = async (values: z.infer<typeof newWorkspaceSchema>) => {
    try {
      await create(values)
    } catch (e) {
      processFormError(form.setError, e, {
        genericMessage:
          'There was a problem creating your workspace. Please try changing the name and try again.',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] p-20">
        <DialogHeader>
          <DialogTitle size="lg">Create a Workspace</DialogTitle>
          <DialogDescription>
            Workspaces help you organize channels, scorecards, and goals.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-3">
            <FormErrorAlert />
            <FormFieldInput
              control={form.control}
              name="name"
              size="lg"
              placeholder="Workspace name (e.g., Customer Support)"
            />
            <Button
              type="submit"
              size="lg"
              loading={isPending}
              loadingLabel="Creating..."
            >
              Create Workspace <Icon name="arrow-right" size={20} />
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
